import React, { useState, useEffect } from 'react';
import { MapPin, Phone, Clock, Mail, Send, Calendar } from 'lucide-react';
import { businessInfo, services } from '../data/mock';
import { useLanguage } from '../context/LanguageContext';
import { toast } from 'sonner';
import Reveal from './motion/Reveal';

const Contact = () => {
  const { t } = useLanguage();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    service: '',
    date: '',
    message: ''
  });

  const [loading, setLoading] = useState(false);
  const [showSquareWidget, setShowSquareWidget] = useState(false);

  // Load Square Appointments widget script
  useEffect(() => {
    if (showSquareWidget && !document.getElementById('square-appointments-script')) {
      const script = document.createElement('script');
      script.id = 'square-appointments-script';
      script.src = 'https://app.squareup.com/appointments/buyer/widget/8z19pdbaqta7bo/LFHGNQ373D5BF.js';
      script.async = true;
      document.body.appendChild(script);
    }
  }, [showSquareWidget]);

  const allServices = [
    ...services.bodyContouring,
    ...services.fajas,
    ...services.beauty
  ];

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    // Simulate form submission
    setTimeout(() => {
      toast.success('Appointment request sent! We will contact you shortly.');
      setFormData({
        name: '',
        email: '',
        phone: '',
        service: '',
        date: '',
        message: ''
      });
      setLoading(false);
    }, 1000);
  };

  return (
    <section id="contact" className="py-24 bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <Reveal className="text-center max-w-3xl mx-auto mb-16 space-y-4">
          <h2 className="text-4xl font-normal text-black">
            {t.contact.title}
          </h2>
          <div className="w-16 h-px bg-black mx-auto"></div>
          
          {/* Square Booking Button */}
          <div className="pt-4">
            <a
              href="https://app.squareup.com/appointments/book/2z9qf4qks9dbay/LFHGNQ373D5BF/start"
              target="_top"
              rel="nofollow"
              className="inline-flex items-center justify-center space-x-2 bg-black text-white px-8 py-4 text-sm font-medium hover:bg-gray-800 transition-all duration-200"
              style={{ borderRadius: '0px', minWidth: '250px', height: '60px' }}
            >
              <Calendar size={20} />
              <span>Book Appointment Online</span>
            </a>
          </div>
        </Reveal>

        <div className="grid lg:grid-cols-2 gap-12">
          {/* Contact Form */}
          <div className="bg-white p-8 lg:p-12 border-2 border-black">
            <h3 className="text-2xl font-normal text-black mb-8">
              {t.contact.form.title}
            </h3>

            <form onSubmit={handleSubmit} className="space-y-6">
              <div>
                <label htmlFor="name" className="block text-sm font-normal text-gray-700 mb-2">
                  {t.contact.form.name} *
                </label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 border border-gray-300 focus:border-gray-900 focus:outline-none focus:ring-1 focus:ring-gray-900 transition-colors duration-200"
                  style={{ borderRadius: '0px' }}
                />
              </div>

              <div>
                <label htmlFor="email" className="block text-sm font-normal text-gray-700 mb-2">
                  {t.contact.form.email}
                </label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  className="w-full px-4 py-3 border border-gray-300 focus:border-gray-900 focus:outline-none focus:ring-1 focus:ring-gray-900 transition-colors duration-200"
                  style={{ borderRadius: '0px' }}
                />
              </div>

              <div>
                <label htmlFor="phone" className="block text-sm font-normal text-gray-700 mb-2">
                  {t.contact.form.phone} *
                </label>
                <input
                  type="tel"
                  id="phone"
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 border border-gray-300 focus:border-gray-900 focus:outline-none focus:ring-1 focus:ring-gray-900 transition-colors duration-200"
                  style={{ borderRadius: '0px' }}
                />
              </div>

              <div>
                <label htmlFor="service" className="block text-sm font-normal text-gray-700 mb-2">
                  {t.contact.form.service}
                </label>
                <select
                  id="service"
                  name="service"
                  value={formData.service}
                  onChange={handleChange}
                  className="w-full px-4 py-3 border border-gray-300 focus:border-gray-900 focus:outline-none focus:ring-1 focus:ring-gray-900 transition-colors duration-200"
                  style={{ borderRadius: '0px' }}
                >
                  <option value="">Select a service...</option>
                  {allServices.map((service) => (
                    <option key={service.id} value={service.id}>
                      {service.name}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label htmlFor="date" className="block text-sm font-normal text-gray-700 mb-2">
                  {t.contact.form.date}
                </label>
                <input
                  type="date"
                  id="date"
                  name="date"
                  value={formData.date}
                  onChange={handleChange}
                  className="w-full px-4 py-3 border border-gray-300 focus:border-gray-900 focus:outline-none focus:ring-1 focus:ring-gray-900 transition-colors duration-200"
                  style={{ borderRadius: '0px' }}
                />
              </div>

              <div>
                <label htmlFor="message" className="block text-sm font-normal text-gray-700 mb-2">
                  {t.contact.form.message}
                </label>
                <textarea
                  id="message"
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  rows={4}
                  className="w-full px-4 py-3 border border-gray-300 focus:border-gray-900 focus:outline-none focus:ring-1 focus:ring-gray-900 transition-colors duration-200 resize-none"
                  style={{ borderRadius: '0px' }}
                />
              </div>

              <button
                type="submit"
                disabled={loading}
                className="w-full bg-gray-900 text-white px-8 py-4 text-sm font-medium hover:bg-gray-800 transition-all duration-200 flex items-center justify-center space-x-2 disabled:opacity-50"
                style={{ borderRadius: '0px', height: '60px' }}
              >
                <Send size={18} />
                <span>{loading ? 'Sending...' : t.contact.form.submit}</span>
              </button>
            </form>
          </div>

          {/* Contact Info & Map */}
          <div className="space-y-8">
            {/* Contact Details */}
            <div className="bg-white p-8 space-y-6">
              <div className="flex items-start space-x-4">
                <div className="w-12 h-12 bg-gray-900 flex items-center justify-center flex-shrink-0">
                  <MapPin size={20} className="text-white" />
                </div>
                <div>
                  <h4 className="text-sm font-medium text-gray-900 mb-1">{t.contact.info.address}</h4>
                  <p className="text-sm text-gray-600">{businessInfo.address}</p>
                </div>
              </div>

              <div className="flex items-start space-x-4">
                <div className="w-12 h-12 bg-gray-900 flex items-center justify-center flex-shrink-0">
                  <Phone size={20} className="text-white" />
                </div>
                <div>
                  <h4 className="text-sm font-medium text-gray-900 mb-1">{t.contact.info.phone}</h4>
                  <a 
                    href={`tel:${businessInfo.phone}`}
                    className="text-sm text-gray-600 hover:text-gray-900 transition-colors duration-200"
                  >
                    {businessInfo.phone}
                  </a>
                </div>
              </div>

              <div className="flex items-start space-x-4">
                <div className="w-12 h-12 bg-gray-900 flex items-center justify-center flex-shrink-0">
                  <Clock size={20} className="text-white" />
                </div>
                <div>
                  <h4 className="text-sm font-medium text-gray-900 mb-1">{t.contact.info.hours}</h4>
                  <p className="text-sm text-gray-600">{businessInfo.hours}</p>
                </div>
              </div>
            </div>

            {/* Google Map */}
            <div className="bg-gray-200 aspect-[4/3] relative border-2 border-black">
              <iframe
                title="Colombian Body Med Spa Location"
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3345.8486969844396!2d-96.64110892344!3d32.91260197359!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x864c1f5b8b8b8b8b%3A0x0!2s3960%20Broadway%20Blvd%20Suite%20205%2C%20Garland%2C%20TX%2075043!5e0!3m2!1sen!2sus!4v1707936000000!5m2!1sen!2sus"
                width="100%"
                height="100%"
                style={{ border: 0 }}
                allowFullScreen=""
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
              ></iframe>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Contact;
